import { cn } from "@ndd/ui";
import { ChevronRight } from "lucide-react";
import * as React from "react";
import type { AppointmentPreviousSoapNoteListed } from "../../../../shared/api-types";
import {
  SIMPLEPRACTICE_IMPORT_LINE,
  formatNoteDate,
  isSimplePracticeImport,
  signedAtLabel,
  splitPreviewLabel,
} from "../../lib/previous-soap-notes-reader";
import { relativeTimeLabel } from "../../lib/relative-time";
import { DRAWER_TEXT } from "./drawer-type-roles";

/**
 * The overview: the last three notes newest first on a rail. It only
 * reads the list response; opening a visit is the caller's decision, so
 * browsing the overview never triggers a note read.
 */
export function PreviousSoapNotesOverview({
  notes,
  timezone,
  now,
  onOpen,
  scrollRef,
  focusIndex = null,
}: {
  notes: AppointmentPreviousSoapNoteListed[];
  timezone: string;
  now: string;
  onOpen: (index: number) => void;
  scrollRef?: React.Ref<HTMLDivElement>;
  /** The visit the reader was opened from; it takes focus when the overview returns. */
  focusIndex?: number | null;
}): React.ReactElement {
  const buttons = React.useRef<(HTMLButtonElement | null)[]>([]);

  // Only the return from the reader moves focus, never a later refresh.
  const returnFocus = React.useRef(focusIndex);

  React.useEffect(() => {
    const index = returnFocus.current;
    if (index !== null) buttons.current[index]?.focus({ preventScroll: true });
  }, []);

  return (
    <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
      <ol className="flex flex-col gap-1 pb-6 pl-6 pr-2.5 pt-3">
        {notes.map((note, index) => (
          <li key={note.id}>
            <OverviewItem
              note={note}
              last={index === notes.length - 1}
              timezone={timezone}
              now={now}
              onOpen={() => onOpen(index)}
              buttonRef={(element) => {
                buttons.current[index] = element;
              }}
            />
          </li>
        ))}
      </ol>
    </div>
  );
}

function OverviewItem({
  note,
  last,
  timezone,
  now,
  onOpen,
  buttonRef,
}: {
  note: AppointmentPreviousSoapNoteListed;
  last: boolean;
  timezone: string;
  now: string;
  onOpen: () => void;
  buttonRef: React.Ref<HTMLButtonElement>;
}): React.ReactElement {
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onOpen}
      className="group flex w-full items-start text-left focus-visible:outline-none"
    >
      <span aria-hidden="true" className="relative w-3 shrink-0 self-stretch">
        <span className="absolute left-0 top-[14.5px] size-[11px] rounded-full border border-sand-500 bg-card group-hover:border-bark group-hover:bg-foreground" />
        {last ? null : (
          <span className="absolute bottom-[-18px] left-[5px] top-[26px] w-px bg-sand-300" />
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col overflow-clip rounded-md px-2.5 py-2 group-hover:bg-sand-50 group-focus-visible:ring-2 group-focus-visible:ring-ring">
        <span className="flex items-center justify-between">
          <span className={DRAWER_TEXT.timelineDate}>
            {formatNoteDate(note.serviceDateAt, timezone)}
          </span>
          <ChevronRight
            aria-hidden="true"
            className="size-4 shrink-0 text-muted-foreground"
          />
        </span>
        <span className={cn(DRAWER_TEXT.meta, "whitespace-nowrap")}>
          {relativeTimeLabel(note.serviceDateAt, now)} ·{" "}
          {signedAtLabel(note.signedAtBasis)}
        </span>
        {isSimplePracticeImport(note.signedAtBasis) ? (
          <span className={cn(DRAWER_TEXT.preview, "line-clamp-3 pt-1.5")}>
            {SIMPLEPRACTICE_IMPORT_LINE}
          </span>
        ) : (
          <PreviewLine preview={note.preview} />
        )}
      </span>
    </button>
  );
}

function PreviewLine({
  preview,
}: {
  preview: string | null;
}): React.ReactElement | null {
  if (!preview) return null;

  const { label, text } = splitPreviewLabel(preview);

  return (
    <span className={cn(DRAWER_TEXT.preview, "line-clamp-3 pt-1.5")}>
      {label ? (
        <>
          <span className="font-medium text-foreground">{label}</span>{" "}
        </>
      ) : null}
      {text}
    </span>
  );
}
